import { _ } from "../../../../core/polyglot/polyglot.js";
import { centerAlign } from "../../../../core/elements/elementAlignement.js";
import { confirmDeletionDialog } from "../../../../core/widgets/dialogs/dialogs.js";
import {
	cost,
	income,
} from "../../../../models/forecast/ruleModels/AccountType.js";
import { extraLarge, small } from "../../../../core/elements/size/index.js";
import { formatCurrency } from "../../../../core/helpers/currency-formatting-util.js";
import { getCSSVariable } from "../../../../core/helpers/css-util.js";
import { insertBetween, max } from "@foretagsplatsen/core-helpers/array";
import { ViewState } from "../ruleTemplate/RuleTemplatesWidget/RuleTemplatesState.js";
import { Widget } from "@foretagsplatsen/widgetjs";
import AbstractPredictionRuleModel from "../../../../models/forecast/ruleModels/predictionModel/AbstractPredictionRuleModel.js";
import AddRuleTemplateAction from "../ruleTemplate/RuleTemplatesWidget/actions/AddRuleTemplateAction.js";
import BalanceRuleModel from "../../../../models/forecast/ruleModels/BalanceRuleModel.js";
import BulkRuleTemplateAction from "../ruleTemplate/RuleTemplatesWidget/actions/BulkRuleTemplateAction.js";
import ChartWidget from "../../../../core/widgets/ChartWidget/ChartWidget.js";
import container from "../../../../core/elements/container/container.js";
import ForecastedValuesChartStrategy from "./ForecastedValuesChartStrategy.js";
import IsCostAccountRowVisitor from "../../../../models/forecast/instance/IsCostAccountRowVisitor.js";
import makeRuleTemplateModel from "../ruleTemplate/RuleTemplatesWidget/RuleTemplateModel/makeRuleTemplateModel.js";
import ManualEntryRuleTemplatesWidget from "../ruleTemplate/ManualEntryRuleTemplatesWidget/ManualEntryRuleTemplatesWidget.js";
import ManualJournalEntryDialog from "../../../../core/widgets/ManualJournalEntryDialog/ManualJournalEntryDialog.js";
import ManualJournalEntryViewer from "../../../../core/widgets/ManualJournalEntryViewer/ManualJournalEntryViewer.js";
import predictionRuleModelFactory from "../../../../models/forecast/ruleModels/predictionModel/PredictionRuleModelFactory.js";
import regionWidget from "../../../../core/widgets/regionWidget.js";
import RemoveRuleTemplateAction from "../ruleTemplate/RuleTemplatesWidget/actions/RemoveRuleTemplateAction.js";
import RuleTemplate from "../../../../models/forecast/template/RuleTemplate.js";
import RuleTemplatesModel from "../ruleTemplate/RuleTemplatesWidget/RuleTemplateModel/RuleTemplatesModel.js";
import RuleTemplatesWidget from "../ruleTemplate/RuleTemplatesWidget/RuleTemplatesWidget.js";
import spinner from "../../../../core/elements/spinner/spinner.js";
import stacker from "../../../../core/elements/stacker/stacker.js";
import text from "../../../../core/elements/text/text.js";
import UpdateRuleTemplateAction from "../ruleTemplate/RuleTemplatesWidget/actions/UpdateRuleTemplateAction.js";

/**
 * Widget displaying information and actions about a cell of a
 * forecast template.
 *
 * @param {ForecastEditorDocumentState} documentState - State of the ForecastEditor page
 * @param {AbstractRow} row - Row of the cell
 * @param {AbstractColumn} column - Column of the cell
 * @param {boolean} [isCostAccountRow] - For testing purpose only
 * @param {function(object): void} [onReportAndCellSelectionRequest] - Function
 *   called with {report,row,column} as argument when the user wants
 *   this cell of this report to be selected
 * @param {function} [manualJournalEntryTransactionsValidator] - Manual entry transactions
 *   validator. As other validators, the returned value is
 *   ignored, but the function should append errors to the provided collector
 * @param {boolean} [readOnly] - If true the rule templates are only readable and cannot
 *   be edited. When there is no rule, it is not possible to add one. When there
 *   is a rule, it is not possible to edit or delete it. When a rule is inherited
 *   it cannot be overridden
 */
export default class ForecastCellInfoWidget extends Widget {
	constructor({
		documentState,
		row,
		column,
		isCostAccountRow = new IsCostAccountRowVisitor().visit(row),
		onReportAndCellSelectionRequest = ({ _report, _row, _column }) => {},
		manualJournalEntryTransactionsValidator = () => {},
		readOnly,
	}) {
		super(...arguments);

		this._documentState = documentState;
		this._row = row;
		this._column = column;
		this._isCostAccountRow = isCostAccountRow;
		this._onReportAndCellSelectionRequest = onReportAndCellSelectionRequest;

		this._manualJournalEntryTransactionsValidator =
			manualJournalEntryTransactionsValidator;

		this._report = this._documentState.getCurrentReport();

		this._loading = false;

		const ruleTemplateWidgetsState = new ViewState({
			ruleTemplateWidgets: [],
		});

		this._ruleTemplatesWidget = this._makeRuleTemplatesWidget({
			readOnly,
			row,
			ruleTemplateWidgetsState,
		});

		this._manualEntryRuleTemplatesWidget =
			this._makeManualEntryRuleTemplatesWidget({
				readOnly,
				row,
				ruleTemplateWidgetsState,
			});

		this._isReadOnly = readOnly;

		this._body = regionWidget({ content: this._renderBodyOn.bind(this) });
	}

	renderContentOn(html) {
		html.div(
			{
				class: "ForecastCellInfoWidget",
				"data-test": "forecast-cell-info",
			},
			this._renderHeaderOn.bind(this),
			this._renderLineChartWidgetOn.bind(this),
			this._body,
		);
	}

	rulesRendererForIncomeStatement() {
		if (!this._shouldShowRules()) {
			return () => {};
		}

		const widgets = [];

		if (this._shouldShowRuleTemplates())
			widgets.push(this._ruleTemplatesWidget);

		if (this._shouldShowManualEntry())
			widgets.push(this._manualEntryRuleTemplatesWidget);

		return insertBetween({
			array: widgets,
			separator: (html) => html.hr(),
		});
	}

	rulesRendererForBalanceSheet() {
		const rules = this._report.getAvailableRuleModelClasses();
		return rules.map((rule) => rule.rendererForRuleModels(this));
	}

	rendererForManualJournalEntry() {
		return (html) => {
			if (!this._isReadOnly) {
				this._renderManualJournalEntryEditorOn(html);
			}
			this._renderManualJournalEntriesOn(html);
		};
	}

	_renderHeaderOn(html) {
		html.div(
			{ class: "drawer-header" },
			this._renderCellTitleOn.bind(this),
		);
	}

	_renderCellTitleOn(html) {
		html.div(
			{ class: "title" },
			html.h2(this._renderTitleOn.bind(this)),
			html.h3(this._renderSubtitleOn.bind(this)),
		);
	}

	_renderBodyOn(html) {
		html.div(
			{ class: "drawer-body" },
			this._loading
				? container(
						{
							size: extraLarge,
							isBlock: true,
						},
						stacker(
							{
								isBlock: true,
								direction: "vertical",
								size: small,
								align: centerAlign,
							},
							spinner,
							text({ important: true }, _("Loading data…")),
						),
					)
				: this._renderLoadedBodyOn.bind(this),
		);
	}

	_renderLoadedBodyOn(html) {
		html.render(
			this._renderValueOn.bind(this),
			this._renderExtraSectionsOn.bind(this),
		);
	}

	_renderTitleOn(html) {
		if (this._row.isAccountRow()) {
			html.span(
				{ class: "account-number" },
				this._row.getAccountNumber(),
			);
		}

		html.render(this._row.getTitle());
	}

	_renderSubtitleOn(html) {
		html.render(this._column.getTitle());
	}

	_renderLineChartWidgetOn(html) {
		html.render(
			this._removeHorizontalMarginsRenderer(this._makeLineChartWidget()),
		);
	}

	_renderValueOn(html) {
		html.div(
			{ class: "cell-info-value" },
			html.div({ class: "amount" }, this._getFormattedCellData()),
		);
	}

	_getFormattedCellData() {
		const report = this._documentState.getCurrentReport();

		return report.getFormattedData({
			row: this._row,
			column: this._column,
		});
	}

	_renderExtraSectionsOn(html) {
		html.render(this._report.rulesRendererFor(this));

		if (!this._column.isPredictedForecastColumn() && this._row.hasData()) {
			this._renderActualsInfoOn(html);
		}
	}

	_renderManualJournalEntriesOn(html) {
		const ruleTemplates = this._documentState.getManualJournalEntriesFor({
			row: this._row,
			column: this._column,
		});

		const viewers = ruleTemplates.map(
			(ruleTemplate) =>
				new ManualJournalEntryViewer({
					shouldShowEditActions: !this._isReadOnly,
					manualJournalEntryRuleModel: ruleTemplate.getRuleModel(),
					legend: this._getRuleLegend(ruleTemplate),
					onEdit: () =>
						this._showManualJournalEntryDialog({
							ruleTemplate,
						}),
					onDelete: () =>
						this._removeManualJournalEntry({ ruleTemplate }),
					currentAccountNumber: this._row.getAccount().getNumber(),
					onAccountSelectionRequest: ({ accountNumber }) =>
						this._selectCellFromAccountNumber(accountNumber),
				}),
		);

		if (viewers.length === 0) {
			return;
		}

		html.div(
			{ class: "manual-journal-entry-viewers" },
			html.div(
				{ class: "viewers-actions" },
				html.a(
					{
						click: () =>
							viewers.forEach((viewer) => viewer.collapse()),
					},
					_("Collapse all"),
				),
				html.a(
					{
						click: () =>
							viewers.forEach((viewer) => viewer.expand()),
					},
					_("Expand all"),
				),
			),
			viewers,
		);
	}

	_getRuleLegend(ruleTemplate) {
		if (!this._isRuleInherited(ruleTemplate)) return undefined;

		return _("Inherited from {date}", {
			date: ruleTemplate.getStart().asString(),
		});
	}

	_isRuleInherited(ruleTemplate) {
		return !ruleTemplate.getStart().equal(this._column.getStart());
	}

	_renderActualsInfoOn(html) {
		html.div(
			{ class: "drawer-section darker" },
			html.h3(_("Actuals")),
			this._renderTopSectionsOn.bind(this),
		);
	}

	_renderTopSectionsOn(html) {
		const reportColumns = this._row.getReport().getColumns();

		// Associate columns to the value of the row at this column:
		const periodValues = this._row
			.getData()
			.map((data, index) => ({
				column: reportColumns[index],
				value: data,
			}))
			.filter(({ column }) => !column.isPredictedForecastColumn());

		html.div(
			{ class: "tops" },
			this._topSectionRenderer({
				periodValues,
				label: _("Top period:"),
				direction: this._isCostAccountRow ? -1 : 1,
			}),
			this._topSectionRenderer({
				periodValues,
				isIconUpward: false,
				label: _("Worst period:"),
				direction: this._isCostAccountRow ? 1 : -1,
			}),
		);
	}

	/**
	 * Render an upward or downward arrow followed by a label, a
	 * period name and a period value. The value being rendered is
	 * either the min or the max of the values in `periodValues`. The
	 * period name is the title of the column corresponding to the
	 * value.
	 *
	 * @param {{column,value}[]} periodValues - Associate columns to
	 *   the value for this column
	 * @param {renderable} label - The title for the section
	 * @param {number} direction - Either 1 if the max of periodValues
	 *   should be rendered or -1 if it is the min
	 * @param {boolean} [isIconUpward] - Show a downward action iff
	 *   this is false
	 */
	_topSectionRenderer({
		periodValues,
		label,
		direction,
		isIconUpward = true,
	}) {
		const { column, value } = max(
			periodValues,
			(periodValue) => direction * periodValue?.value,
		);

		return (html) =>
			html.div(
				{ class: "top", "data-test": "top" },
				html.div(
					html.img({
						class: { downward: !isIconUpward },
						src: "/Client/img/trend.svg",
					}),
				),
				html.div(
					html.div(
						html.span({ class: "top-label" }, label),
						" ",
						html.span({ class: "top-period" }, column.getTitle()),
					),
					html.div({ class: "top-amount" }, formatCurrency(value)),
				),
			);
	}

	_renderRuleTemplateWidgetOn(html) {
		html.render(this._ruleTemplatesWidget);
	}

	_renderManualJournalEntryEditorOn(html) {
		html.div(
			{ class: "manual-entry-editor-section" },
			html.button(
				{
					"data-test": "add-manual-journal",
					class: "icon-button btn btn-primary icon-and-text",
					click: () => this._showManualJournalEntryDialog(),
				},
				html.i({ class: "far fa-circle-plus" }),
				_("Add manual journal"),
			),
		);
	}

	_removeHorizontalMarginsRenderer(content) {
		return (html) =>
			html.div(
				{
					class: "clearfix",
					style: "margin-left: -30px; margin-right: -30px",
				},
				content,
			);
	}

	_removeManualJournalEntry({ ruleTemplate }) {
		const dialog = confirmDeletionDialog({
			name: ruleTemplate.getRuleModel().getName(),
			callback: () => {
				this._handleSingleAction({
					action: this._makeRemoveActionFor({
						ruleTemplate,
					}),
				});
			},
		});
		dialog.show();
	}

	_makeLineChartWidget() {
		const report = this._row.getReport();

		const indexOfFirstPredictedColumn = report
			.getColumns()
			.findIndex((column) => column.isPredictedForecastColumn());

		const rowValues = this._row.getData();

		return new ChartWidget({
			chartStrategy: new ForecastedValuesChartStrategy({
				labels: report.getColumns().map((column) => column.getTitle()),
				indexOfFirstPredictedColumn,
				selectedPeriodIndex: report.getColumns().indexOf(this._column),
			}),
			values: rowValues,
			height: getCSSVariable("--forecast-cell-info-chart-height-in-px"),
			dataTestName: "line-chart",
		});
	}

	_makeRuleTemplatesWidget({ readOnly, row, ruleTemplateWidgetsState }) {
		const ruleTemplatesModel = this._makeRuleTemplatesModel();

		const widget = new RuleTemplatesWidget({
			availableRuleModelClasses:
				this._report.getAvailableRuleModelClasses({ row }),
			ruleTemplatesModel,
			budgets: this._documentState.getBudgets(),
			vatRates: this._documentState.getVatRates(),
			onCellSelectionRequest: this._onCellSelectionRequest.bind(this),
			onBulkActionRequest: this._onBulkActionRequest({
				getWidget: () => widget,
			}),
			readOnly,
			currentState: ruleTemplateWidgetsState,
		});

		return widget;
	}

	_makeManualEntryRuleTemplatesWidget({
		readOnly,
		row,
		ruleTemplateWidgetsState,
	}) {
		const ruleTemplateModels = this._documentState.getManualEntriesFor({
			row: this._row,
			column: this._column,
		});

		const widget = new ManualEntryRuleTemplatesWidget({
			row,
			column: this._column,
			ruleTemplateModels,
			budgets: this._documentState.getBudgets(),
			vatRates: this._documentState.getVatRates(),
			paymentSpread: this._getPaymentSpread(),
			onBulkActionRequest: this._onManualEntryBulkActionRequest({
				getWidget: () => widget,
			}),
			readOnly,
			currentState: ruleTemplateWidgetsState,
			isCostAccountRow: this._isCostAccountRow,
		});

		return widget;
	}

	_showManualJournalEntryDialog({ ruleTemplate } = {}) {
		const allAccounts = this._documentState.getAllAccounts();
		const selectedAccount = this._row.getAccount();
		const currentMonth =
			ruleTemplate?.getStart().asDate() ||
			this._column.getPeriod().getStart();

		// we make a new dialog to not keep state from one opening to another
		const dialog = new ManualJournalEntryDialog({
			allAccounts,
			selectedAccount,
			manualJournalEntryRuleModel: ruleTemplate?.getRuleModel(),
			currentMonth,
			onSubmit: (manualJournalEntryModel) =>
				this._submitManualJournalEntryModel({
					ruleTemplate,
					manualJournalEntryModel,
				}),
			extraValidator: this._manualJournalEntryTransactionsValidator,
		});

		dialog.show();
	}

	_submitManualJournalEntryModel({ ruleTemplate, manualJournalEntryModel }) {
		return this._handleSingleAction({
			action: this._makeAddOrUpdateActionFor({
				ruleTemplate,
				manualJournalEntryModel,
			}),
		});
	}

	_makeAddOrUpdateActionFor({ ruleTemplate, manualJournalEntryModel }) {
		if (ruleTemplate) {
			ruleTemplate.setRuleModel(manualJournalEntryModel);
			return new UpdateRuleTemplateAction({ ruleTemplate });
		}

		const newRuleTemplate = this._makeRuleTemplate({
			ruleModel: manualJournalEntryModel,
		});
		return new AddRuleTemplateAction({
			ruleTemplate: newRuleTemplate,
		});
	}

	_makeRemoveActionFor({ ruleTemplate }) {
		return new RemoveRuleTemplateAction({ ruleTemplate });
	}

	_shouldShowRules() {
		return (
			this._column.isPredictedForecastColumn() &&
			!this._row.isFormulaRow()
		);
	}

	_shouldShowRuleTemplates() {
		if (!this._isReadOnly) return true;

		return !this._ruleTemplatesWidget.isEmpty();
	}

	_shouldShowManualEntry() {
		if (this._isReadOnly && this._manualEntryRuleTemplatesWidget.isEmpty())
			return false;

		return (
			this._row.isAccountRow() && this._column.isPredictedForecastColumn()
		);
	}

	_onBulkActionRequest({ getWidget }) {
		return async ({ bulkAction }) => {
			const widget = getWidget();

			await this._showLoadingStateWhile(() =>
				this._documentState.bulkEditTemplates(bulkAction),
			);

			const ruleTemplatesModel = this._makeRuleTemplatesModel();
			widget.setRuleTemplatesModel(ruleTemplatesModel);
		};
	}

	_onManualEntryBulkActionRequest({ getWidget }) {
		return async ({ bulkAction }) => {
			await this._showLoadingStateWhile(() =>
				this._documentState.bulkEditTemplates(bulkAction),
			);

			const widget = getWidget();

			const ruleTemplateModels = this._documentState.getManualEntriesFor({
				row: this._row,
				column: this._column,
			});

			widget.setRuleTemplateModels(ruleTemplateModels);
		};
	}

	async _showLoadingStateWhile(fn) {
		this._loading = true;
		this._body.update();
		await fn();
		this._loading = false;
		this._body.update();
	}

	async _handleSingleAction({ action }) {
		const bulkAction = new BulkRuleTemplateAction();
		bulkAction.appendActions([action]);

		await this._showLoadingStateWhile(() =>
			this._documentState.bulkEditTemplates(bulkAction),
		);
	}

	_makeRuleTemplatesModel() {
		const predictionRuleTemplateModel = makeRuleTemplateModel({
			column: this._column,
			row: this._row,
			documentState: this._documentState,
			ruleModelClass: AbstractPredictionRuleModel,
			defaultRuleTemplate: this._makeRuleTemplate({
				ruleModel: predictionRuleModelFactory.makeDefault(),
			}),
		});

		const balanceRuleTemplateModel = makeRuleTemplateModel({
			column: this._column,
			row: this._row,
			documentState: this._documentState,
			ruleModelClass: BalanceRuleModel,
			defaultRuleTemplate: this._makeRuleTemplate({
				ruleModel: new BalanceRuleModel({
					accountType: this._isCostAccountRow ? cost : income,
					paymentSpread: this._getPaymentSpread(),
				}),
			}),
		});

		return new RuleTemplatesModel({
			predictionRuleTemplateModel,
			balanceRuleTemplateModel,
		});
	}

	_makeRuleTemplate({ ruleModel }) {
		const rowId = this._row.getStableId();
		const start = this._column.getStart();

		return new RuleTemplate({
			rowId,
			start,
			ruleModel,
		});
	}

	_getPaymentSpread() {
		return this._isCostAccountRow
			? this._documentState
					.getConfiguration()
					.getForecastAccountsPayableDelays()
			: this._documentState
					.getConfiguration()
					.getForecastAccountsReceivableDelays();
	}

	_selectCellFromAccountNumber(accountNumber) {
		const row = this._documentState.getRowForAccountNumber(accountNumber);
		const report = this._documentState.getReportFromId(row.getReportId());
		const column = report.getColumn({ start: this._column.getStart() });

		this._onReportAndCellSelectionRequest({ report, row, column });
	}

	_onCellSelectionRequest({ row, column }) {
		const report = this._documentState.getReportFromId(row.getReportId());

		this._onReportAndCellSelectionRequest({ report, row, column });
	}
}
